// One case: its retention (fixed when it closed), the folder linked to it,
// and its records — what was filed, when, by whom, its checksum, the
// signatures as verified then and its archival copy. Managers close and
// reopen it, unlink its folder and withdraw a mistaken filing.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, CheckCircle2, FileText, Flame, Loader2, Lock, LockOpen, RotateCcw, Undo2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/modules/console/components/ConfirmDialog.jsx';
import { records } from '../lib/api.js';
import { fmtDay } from '../lib/journals.js';
import { periodLabel, titleIn } from '../lib/retention.js';
import { ExtendDialog, ReasonDialog } from './dialogs.jsx';

const PRESERVE_TONE = {
  done: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  pending: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  failed: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  not_needed: 'border-border bg-muted/50 text-muted-foreground',
  unsupported: 'border-border bg-muted/50 text-muted-foreground',
};

export function CaseSheet({ caseUuid, canManage, canReview = false, onOpenChange, onChanged }) {
  const { t, i18n } = useTranslation('records');
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'close' | 'reopen' | 'unlink' | 'hold' | 'release' | {withdraw: record}
  const [extending, setExtending] = useState(null);

  const load = useCallback(async () => {
    const res = await records.caseDetail(caseUuid);
    if (res.success) setDetail(res.response);
    else toast.error(res.message);
  }, [caseUuid]);

  useEffect(() => {
    setDetail(null);
    if (caseUuid) load();
  }, [caseUuid, load]);

  const after = async (res, message) => {
    if (res.success) {
      if (message) toast.success(message);
      await load();
      onChanged?.();
    } else {
      toast.error(res.message);
    }
  };

  const open = detail?.status === 'open';
  const destroyed = detail?.status === 'destroyed';
  const live = (detail?.records || []).filter((r) => !r.withdrawn_at);
  const withdrawn = (detail?.records || []).filter((r) => r.withdrawn_at);

  return (
    <Sheet open={Boolean(caseUuid)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="case-sheet">
        {!detail ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <span className="font-mono">{detail.case_index}</span>
                <Badge variant="outline" data-testid="case-status">{t(`case.statuses.${detail.status}`)}</Badge>
              </SheetTitle>
              <SheetDescription>{detail.title}{detail.unit ? ` · ${detail.unit}` : ''} · {detail.year}</SheetDescription>
            </SheetHeader>

            {detail.legal_hold_reason && (
              <p className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2 text-sm text-red-800 dark:text-red-300" data-testid="case-hold">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('review.holdBanner', { reason: detail.legal_hold_reason, date: fmtDay(detail.legal_hold_at) })}</span>
              </p>
            )}
            {destroyed && (
              <p className="flex items-start gap-2 rounded-md border border-border bg-muted p-2 text-sm text-foreground" data-testid="case-destroyed">
                <Flame className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('review.destroyedBanner', { date: fmtDay(detail.destroyed_at), number: detail.act_number })}</span>
              </p>
            )}
            {detail.status === 'transferred' && (
              <p className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-2 text-sm text-blue-800 dark:text-blue-300" data-testid="case-transferred">
                {t('transfer.banner', { number: detail.act_number })}
              </p>
            )}
            {detail.status === 'in_review' && detail.act_number && (
              <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 text-sm text-amber-800 dark:text-amber-300">{t('review.onAct', { number: detail.act_number })}</p>
            )}
            <div className="grid gap-3 rounded-md border bg-muted/40 p-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">{t('case.retention')}</p>
                <p>{periodLabel(detail.retention, t)} · {t('case.article', { article: detail.retention.article })}</p>
                <p className="text-xs text-muted-foreground">{titleIn(detail.retention.title, i18n.language)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('case.keptUntil')}</p>
                <p data-testid="case-retention-until">
                  {detail.status === 'open'
                    ? t('case.fixedOnClose')
                    : detail.retention_until ? fmtDay(detail.retention_until) : t('case.noEnd')}
                </p>
                {detail.closed_at && (
                  <p className="text-xs text-muted-foreground">{t('case.closedOn', { date: fmtDay(detail.closed_at) })}</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground">{t('case.folder')}</p>
                <div className="flex items-center justify-between gap-2">
                  <span>{detail.folder_name || t('case.noFolder')}</span>
                  {canManage && open && detail.folder_uuid && (
                    <Button variant="ghost" size="sm" className="h-7 gap-1" onClick={() => setConfirm('unlink')}>
                      <Unlink className="h-3.5 w-3.5" /> {t('case.unlink')}
                    </Button>
                  )}
                </div>
                {!detail.folder_uuid && open && <p className="text-xs text-muted-foreground">{t('case.linkHint')}</p>}
              </div>
            </div>

            {(canManage || canReview) && !destroyed && detail.status !== 'transferred' && (
              <div className="flex flex-wrap gap-2">
                {canReview && detail.status === 'closed' && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setExtending(detail)} data-testid="case-extend">
                    <CalendarPlus className="h-3.5 w-3.5" /> {t('review.extend')}
                  </Button>
                )}
                {canReview && (detail.legal_hold_reason ? (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirm('release')} data-testid="case-release">
                    <LockOpen className="h-3.5 w-3.5" /> {t('review.release')}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirm('hold')} data-testid="case-hold-set">
                    <Lock className="h-3.5 w-3.5" /> {t('review.hold')}
                  </Button>
                ))}
              </div>
            )}
            {canManage && !destroyed && detail.status !== 'in_review' && (
              <div className="flex flex-wrap gap-2">
                {open ? (
                  <Button size="sm" className="gap-1.5" onClick={() => setConfirm('close')} data-testid="case-close">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {t('case.close')}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirm('reopen')} data-testid="case-reopen">
                    <RotateCcw className="h-3.5 w-3.5" /> {t('case.reopen')}
                  </Button>
                )}
              </div>
            )}

            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('case.records', { count: live.length })}</h3>
              {live.length === 0 ? (
                <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">{t('case.noRecords')}</p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {live.map((r) => (
                    <li key={r.uuid} className="space-y-1 px-3 py-2 text-sm" data-testid={`record-${r.sequence}`}>
                      <div className="flex items-start gap-2">
                        <span className="w-6 shrink-0 text-xs text-muted-foreground">{r.sequence}</span>
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className={cn('flex-1', !r.file_exists && 'text-muted-foreground line-through')}>{r.name}</span>
                        {r.destroyed_at && <Badge variant="outline">{t('review.destroyed')}</Badge>}
                        {['mismatch', 'missing'].includes(r.fixity_status) && (
                          <Badge variant="outline" className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300" data-testid={`fixity-${r.sequence}`}>
                            {t(`integrity.fixity.${r.fixity_status}`)}
                          </Badge>
                        )}
                        <Badge variant="outline" className={PRESERVE_TONE[r.preservation_status]} title={r.preservation_error || undefined}>
                          {t(`case.preservation.${r.preservation_status}`, { format: r.preservation_format || 'PDF' })}
                        </Badge>
                        {canManage && open && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('case.withdraw')}
                            onClick={() => setConfirm({ withdraw: r })}>
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <p className="pl-8 text-xs text-muted-foreground">
                        {t(`case.sources.${r.source}`)} · {fmtDay(r.filed_at)}{r.filed_by_name ? ` · ${r.filed_by_name}` : ''}
                        {r.registration_number ? ` · № ${r.registration_number}` : ''}
                      </p>
                      {r.signatures.length > 0 && (
                        <p className="pl-8 text-xs text-muted-foreground">
                          <Lock className="mr-1 inline h-3 w-3" />
                          {t('case.signatures', { list: r.signatures.map((s) => s.signer || '—').join(', ') })}
                        </p>
                      )}
                      {r.sha256 && <p className="pl-8 font-mono text-[10px] text-muted-foreground" title={r.sha256}>SHA-256 {r.sha256.slice(0, 16)}…</p>}
                    </li>
                  ))}
                </ul>
              )}
              {withdrawn.length > 0 && (
                <details className="mt-2 text-xs text-muted-foreground">
                  <summary className="cursor-pointer">{t('case.withdrawnList', { count: withdrawn.length })}</summary>
                  <ul className="mt-1 space-y-1 pl-4">
                    {withdrawn.map((r) => <li key={r.uuid}>{r.name} — {r.withdraw_reason} ({fmtDay(r.withdrawn_at)})</li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        <ConfirmDialog
          open={confirm === 'close'}
          onOpenChange={(o) => !o && setConfirm(null)}
          title={t('case.closeTitle', { index: detail?.case_index })}
          description={t('case.closeDescription')}
          confirmLabel={t('case.close')}
          onConfirm={async () => after(await records.closeCase(caseUuid), t('case.closed'))}
        />
        <ConfirmDialog
          open={confirm === 'unlink'}
          onOpenChange={(o) => !o && setConfirm(null)}
          title={t('case.unlinkTitle', { name: detail?.folder_name })}
          confirmLabel={t('case.unlink')}
          onConfirm={async () => after(await records.linkFolder(caseUuid, null))}
        />
        <ReasonDialog
          open={confirm === 'reopen'}
          title={t('case.reopenTitle', { index: detail?.case_index })}
          description={t('case.reopenDescription')}
          confirmLabel={t('case.reopen')}
          onOpenChange={(o) => !o && setConfirm(null)}
          onConfirm={async (reason) => after(await records.reopenCase(caseUuid, reason), t('case.reopened'))}
        />
        <ReasonDialog
          open={confirm === 'hold'}
          title={t('review.holdTitle', { index: detail?.case_index })}
          description={t('review.holdDescription')}
          confirmLabel={t('review.hold')}
          onOpenChange={(o) => !o && setConfirm(null)}
          onConfirm={async (reason) => after(await records.hold(caseUuid, reason), t('review.held'))}
        />
        <ReasonDialog
          open={confirm === 'release'}
          title={t('review.releaseTitle', { index: detail?.case_index })}
          confirmLabel={t('review.release')}
          onOpenChange={(o) => !o && setConfirm(null)}
          onConfirm={async (reason) => after(await records.release(caseUuid, reason), t('review.released'))}
        />
        <ExtendDialog target={extending} onOpenChange={(o) => !o && setExtending(null)} onDone={() => { load(); onChanged?.(); }} />
        <ReasonDialog
          open={Boolean(confirm?.withdraw)}
          title={t('case.withdrawTitle', { name: confirm?.withdraw?.name })}
          description={t('case.withdrawDescription')}
          confirmLabel={t('case.withdraw')}
          onOpenChange={(o) => !o && setConfirm(null)}
          onConfirm={async (reason) => after(await records.withdraw(confirm.withdraw.uuid, reason), t('case.withdrawn'))}
        />
      </SheetContent>
    </Sheet>
  );
}
